import {BlockedIcon} from '@primer/octicons-react'
import {CounterLabel, Link, Token} from '@primer/react'
import {clsx} from 'clsx'
import React from 'react'

import styles from './IssueTree.module.css'

const TreeContext = React.createContext<{
  hasTrailingVisuals: boolean | undefined
}>({
  hasTrailingVisuals: undefined,
})

const ItemContext = React.createContext<{
  level: number
}>({
  level: 0,
})

type TreeProps = React.PropsWithChildren<{
  subIssueCount: number
  subIssueHeaderText?: string
  hasTrailingVisuals?: boolean
}>

function Tree({subIssueCount, subIssueHeaderText = 'Sub-issues', hasTrailingVisuals = false, children}: TreeProps) {
  const childrenArray = React.Children.toArray(children)
  const rootItem = childrenArray.find(
    (child): child is React.ReactElement<React.ComponentProps<typeof TreeRootItem>> =>
      React.isValidElement(child) && child.type === TreeRootItem,
  )
  const items = childrenArray.filter(
    (child): child is React.ReactElement<React.ComponentProps<typeof TreeItem>> =>
      React.isValidElement(child) && child.type === TreeItem,
  )

  // Mark the last item to handle rounded corners and padding
  const lastItemsIndex = items.length - 1
  if (lastItemsIndex >= 0 && React.isValidElement(items[lastItemsIndex])) {
    items[lastItemsIndex] = React.cloneElement(items[lastItemsIndex], {isLastBranch: true})
  }

  const providerProps = React.useMemo(() => ({hasTrailingVisuals}), [hasTrailingVisuals])

  // Flags whether the root item is followed by the sub-issues sub-header, replacing
  // `.rootItem:has(+ .subHeader)` so streaming updates don't trigger WebKit `:has()` invalidation.
  const rootItemWithSubIssues =
    rootItem && React.isValidElement(rootItem)
      ? React.cloneElement(rootItem, {hasSubIssues: items.length > 0})
      : rootItem

  return (
    <TreeContext value={providerProps}>
      <div className={styles.container} data-has-trailing-visual={hasTrailingVisuals || undefined}>
        {rootItemWithSubIssues}

        {items.length > 0 && (
          <>
            <div className={styles.subHeader}>
              {subIssueHeaderText}
              <CounterLabel>{subIssueCount}</CounterLabel>
            </div>
            <ul role="tree" className={styles.tree}>
              {items}
            </ul>
          </>
        )}
      </div>
    </TreeContext>
  )
}

type TreeRootItemProps = TreeItemContentProps & {hasSubIssues?: boolean}

function TreeRootItem({hasSubIssues, ...props}: TreeRootItemProps) {
  const providerProps = React.useMemo(() => ({level: 0}), [])

  return (
    <ItemContext value={providerProps}>
      <div className={clsx(styles.item, styles.rootItem, hasSubIssues && styles.rootItemHasSubIssues)}>
        <TreeItemContent {...props} />
      </div>
    </ItemContext>
  )
}

type TreeItemProps = React.PropsWithChildren<TreeItemContentProps> & {
  /**
   * Indicates this as the last branch at its level. A data attribute will be applied
   * to the deepest item in this branch (either this item if it has no children, or
   * recursively to its last child) for visual styling.
   */
  isLastBranch?: boolean
}

function TreeItem({children, isLastBranch, ...props}: TreeItemProps) {
  const childrenArray = React.Children.toArray(children)
  const nestedItems = childrenArray.filter(
    (child): child is React.ReactElement<React.ComponentProps<typeof TreeItem>> =>
      React.isValidElement(child) && child.type === TreeItem,
  )
  const contentChildren = childrenArray.filter(child => !(React.isValidElement(child) && child.type === TreeItem))

  const {level} = React.use(ItemContext)
  const providerProps = React.useMemo(() => ({level: level + 1}), [level])

  const isLastRendered = isLastBranch && nestedItems.length === 0

  // If this item has nested items, the last nested item should get the prop
  const lastNestedIndex = nestedItems.length - 1
  if (isLastBranch && lastNestedIndex >= 0 && React.isValidElement(nestedItems[lastNestedIndex])) {
    nestedItems[lastNestedIndex] = React.cloneElement(nestedItems[lastNestedIndex], {isLastBranch: true})
  }

  return (
    <ItemContext value={providerProps}>
      <li
        role="treeitem"
        aria-selected="false"
        className={styles.item}
        data-is-last-rendered={isLastRendered || undefined} // Mark the last rendered item for styling
      >
        <TreeItemContent {...props}>{contentChildren}</TreeItemContent>
        {nestedItems.length > 0 && (
          <ul role="group" className={styles.tree}>
            {nestedItems}
          </ul>
        )}
      </li>
    </ItemContext>
  )
}

type TreeItemContentProps = {
  leadingVisual?: React.ReactNode
  title: string
  description?: React.ReactNode
  trailingBadge?: React.ReactNode
  trailingVisual?: React.ReactNode
  href?: string
  onClick?: (event: React.MouseEvent) => void
  blockedBy?: BlockedByDependencies[]
}
function TreeItemContent({
  leadingVisual,
  title,
  description,
  trailingBadge,
  trailingVisual,
  href,
  onClick,
  blockedBy,
}: TreeItemContentProps) {
  const {level} = React.use(ItemContext)
  const {hasTrailingVisuals} = React.use(TreeContext)
  const hasDependencies = blockedBy != null && blockedBy.length > 0
  // Drives grid layout / dividers in place of `:has(.description)` and `:has(+ .description)`
  // so streaming updates don't trigger WebKit `:has()` invalidation.
  const hasDescription = description != null || hasDependencies

  return (
    <>
      <div
        className={clsx(styles.grid, hasDescription && styles.hasDescription)}
        style={{'--level': level}}
        data-has-onclick={onClick ? true : undefined}
      >
        {level > 0 && <div className={styles.spacer} />}
        <div className={styles.leadingVisual}>{leadingVisual}</div>
        <div className={clsx(styles.content, hasDescription && styles.hasDescription)}>
          <Link as={href ? 'a' : onClick ? 'button' : 'span'} className={styles.title} href={href} onClick={onClick}>
            {title}
          </Link>
          {trailingBadge}
        </div>
        {hasDescription && (
          <div className={styles.description}>
            {hasDependencies && <IssueDependencies dependencyType="Blocked by" blockedBy={blockedBy} />}
            {description}
          </div>
        )}
        {hasTrailingVisuals && <div className={styles.trailingVisual}>{trailingVisual}</div>}
      </div>
    </>
  )
}

export type BlockedByDependencies = {
  title: string
  href?: string
  onClick?: (event: React.MouseEvent) => void
}

interface IssueDependenciesProps {
  dependencyType: 'Blocked by'
  blockedBy: BlockedByDependencies[]
}
function IssueDependencies({dependencyType, blockedBy}: IssueDependenciesProps) {
  if (!blockedBy.length) return null

  const blockedByIssueLink = (
    <Link
      data-testid="blocked-by-link"
      className={styles.blockedBy}
      muted
      as={blockedBy[0]?.href ? 'a' : blockedBy[0]?.onClick ? 'button' : 'span'}
      href={blockedBy[0]?.href}
      onClick={blockedBy[0]?.onClick}
      title={blockedBy[0]?.title} // Add full title as tooltip
    >
      {blockedBy[0]?.title}
    </Link>
  )

  return (
    <Token
      size="medium"
      className="mr-2"
      leadingVisual={() => <BlockedIcon data-testid="blocked-by-icon" className="fgColor-danger" size={14} />}
      text={
        blockedBy.length > 1 ? (
          <>
            <span className={styles.tokenContainer}>
              {dependencyType} {blockedBy.length}
            </span>
          </>
        ) : (
          <>
            <span className={styles.tokenContainer}>
              {dependencyType}: {blockedByIssueLink}
            </span>
          </>
        )
      }
    />
  )
}

export const IssueTree = Object.assign(Tree, {
  RootItem: TreeRootItem,
  Item: TreeItem,
})
